import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, X, BellRing, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useDoubleConfirm } from "@/components/ConfirmProvider";

export default function DeletionRequests() {
  const { refresh } = useOutletContext();
  const [reqs, setReqs] = useState([]);
  const confirm = useDoubleConfirm();
  const load = () => api.get("/deletion-requests").then((r) => {
    setReqs(r.data);
    window.dispatchEvent(new Event("richieste-cancellazione-aggiornate"));
  });
  useEffect(() => { load(); }, []);

  const approve = async (r) => {
    const title = r.target_name || r.document_name || "";
    const ok = await confirm({
      variant: "approve",
      title: r.type === "document_replace" ? "Approva sostituzione file" : "Approva eliminazione",
      detail: title,
    });
    if (!ok) return;
    await api.post(`/deletion-requests/${r.id}/approve`);
    toast.success("Richiesta approvata");
    load(); refresh?.();
  };
  const reject = async (r) => {
    const ok = await confirm({
      variant: "delete",
      title: "Rifiuta richiesta",
      message: "La richiesta verrà rifiutata.\nProcedere?",
      detail: r.target_name || r.document_name,
      confirmLabel: "SI, rifiuta",
    });
    if (!ok) return;
    await api.post(`/deletion-requests/${r.id}/reject`);
    toast.info("Richiesta rifiutata");
    load();
  };

  return (
    <div className="space-y-5" data-testid="deletion-requests-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight">Richieste di cancellazione</h1>
        <p className="text-slate-500 mt-1">Approva o rifiuta le richieste inviate dal personale di amministrazione.</p>
      </div>

      <Card className="border border-slate-200 shadow-sm bg-white">
        {reqs.length === 0 ? (
          <div className="py-16 text-center text-slate-400">
            <BellRing className="h-10 w-10 mx-auto mb-2 text-slate-300" />
            <div>Nessuna richiesta pendente</div>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {reqs.map((r) => {
              const isPratica = r.type === "pratica";
              const isReplace = r.type === "document_replace";
              const title = r.target_name || r.document_name || "—";
              const badge = isPratica ? { label: "PRATICA", cls: "bg-amber-100 text-amber-700" }
                : isReplace ? { label: "SOSTITUZIONE FILE", cls: "bg-indigo-100 text-indigo-700" }
                : { label: "DOCUMENTO", cls: "bg-blue-100 text-blue-700" };
              return (
                <div key={r.id} className="flex items-center gap-3 px-5 py-4">
                  <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${isReplace ? "bg-indigo-50 text-indigo-600" : "bg-rose-50 text-rose-600"}`}>
                    <Trash2 className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${badge.cls}`}>{badge.label}</span>
                      <span className="font-medium truncate">{title}</span>
                    </div>
                    {isReplace && r.new_filename && (
                      <div className="text-xs text-slate-600 mt-0.5">
                        Nuovo: <span className="font-medium">{r.new_filename}</span> ({(r.new_size / 1024).toFixed(1)} KB)
                      </div>
                    )}
                    <div className="text-xs text-slate-500 mt-0.5">Richiesta da {r.requested_by_name} · {new Date(r.created_at).toLocaleString("it-IT")}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => reject(r)} data-testid={`reject-${r.id}`}><X className="h-4 w-4 mr-1" /> Rifiuta</Button>
                  <Button size="sm" onClick={() => approve(r)} className={isReplace ? "bg-indigo-600 hover:bg-indigo-700" : "bg-rose-600 hover:bg-rose-700"} data-testid={`approve-${r.id}`}>
                    <Check className="h-4 w-4 mr-1" /> Approva
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
